import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SEO from "../components/SEO";
import { useGoogleReviews, writeReviewUrl } from "../hooks/useGoogleReviews";
import {
  HardHat, Droplets, Layers, Paintbrush, Hammer,
  Star, MapPin, Phone, MessageCircle, Mail,
  Shield, Clock, Award, Users, ChevronRight, ChevronLeft, ExternalLink, PenLine
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/kkz81rty_IMG_0034.jpeg",
};

// Service images for carousels - matched to service pages
const serviceImages = {
  aannemer: [
    "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/z0dfbfme_IMG_0007.jpeg",
    "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/kx9l7t85_IMG_0504.jpeg",
    "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/sz5cfhqw_IMG_0009.jpeg",
    "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/gbul91vb_IMG_0022.jpeg",
  ],
  badkamer: [
    "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/6t3nje3k_IMG_0009.jpeg",
    "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/ss6esrjy_IMG_0010.jpeg",
    "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/dnlzm1vx_IMG_0001.jpeg",
    "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/uyja0qe2_IMG_0002.jpeg",
  ],
  stukadoor: [
    "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/f8e401si_IMG_1151.jpeg",
    "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/0k4pxmat_IMG_1152.jpeg",
    "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/rqj81ozb_IMG_1154.jpeg",
    "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/9hr3mjnp_3902f7c4-02b6-4427-b91c-a0e000430543.jpeg",
  ],
  schilderwerk: [
    "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/fmc12avf_IMG_6954.jpeg",
    "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/774w7dt3_IMG_6960.jpeg",
    "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/lxb1niow_0b9a96ba-fb50-4805-bb07-d74486690f1a.jpeg",
    "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/3uw1dn9p_IMG_6958.jpeg",
  ],
  timmerwerk: [
    "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/jbd70svw_7d1af8a2-a6b1-4a70-86d4-5faf3f33749f.jpeg",
    "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/5mg034ry_61c7164a-e3f0-41bd-b959-98927f5eb8e9.jpeg",
    "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/y59rovtn_555a74e4-61c2-499c-80ca-694ddf44f650.jpeg",
    "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/4n5u60q0_6a573654-d445-47cb-9f1d-dccc33def286.jpeg",
  ],
};

const services = [
  {
    title: "Aannemer",
    desc: "Complete bouwprojecten en renovaties van A tot Z.",
    icon: HardHat,
    serviceKey: "aannemer",
    path: "/aannemer",
    span: "lg:col-span-8",
  },
  {
    title: "Badkamer Renovatie",
    desc: "Complete badkamer renovaties inclusief sloopwerk, leidingen, tegelwerk en sanitair.",
    icon: Droplets,
    serviceKey: "badkamer",
    path: "/badkamer-renovatie",
    span: "lg:col-span-4",
  },
  {
    title: "Stukadoor",
    desc: "Professioneel stukadoor werk voor muren, plafonds en renovatie stucwerk.",
    icon: Layers,
    serviceKey: "stukadoor",
    path: "/stukadoor",
    span: "lg:col-span-4",
  },
  {
    title: "Schilderwerk",
    desc: "Binnen en buiten schilderwerk met hoogwaardige materialen.",
    icon: Paintbrush,
    serviceKey: "schilderwerk",
    path: "/schilderwerk",
    span: "lg:col-span-4",
  },
  {
    title: "Timmerwerk",
    desc: "Kozijnen, deuren, maatwerk meubels en renovatie timmerwerk.",
    icon: Hammer,
    serviceKey: "timmerwerk",
    path: "/timmerwerk",
    span: "lg:col-span-4",
  },
];

// Mini carousel component for service cards
function ServiceCardCarousel({ images, title }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const prevSlide = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const handleNext = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    nextSlide();
  }, [nextSlide]);

  // Auto-play always (pause on manual interaction)
  useEffect(() => {
    if (!isPaused && images.length > 1) {
      intervalRef.current = setInterval(nextSlide, 3500);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPaused, nextSlide, images.length]);

  // Pause briefly after manual navigation
  const handleManualNav = useCallback((e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    setIsPaused(true);
    if (direction === 'next') {
      nextSlide();
    } else {
      setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    }
    // Resume auto-play after 5 seconds
    setTimeout(() => setIsPaused(false), 5000);
  }, [nextSlide, images.length]);

  return (
    <div
      className="relative h-56 overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Images */}
      {images.map((img, i) => (
        <img
          key={i}
          src={img}
          alt={`${title} Breukelen - Cardoso Bouw en Onderhoud ${i + 1}`}
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
            i === currentIndex ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
      
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
      
      {/* Navigation arrows - show on hover */}
      <div className={`absolute inset-0 flex items-center justify-between px-2 transition-opacity duration-300 ${isHovered ? "opacity-100" : "opacity-0"}`}>
        <button
          onClick={(e) => handleManualNav(e, 'prev')}
          className="w-8 h-8 bg-[#005F5F]/80 hover:bg-[#C5A059] text-white flex items-center justify-center transition-colors z-10"
          aria-label="Vorige afbeelding"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={(e) => handleManualNav(e, 'next')}
          className="w-8 h-8 bg-[#005F5F]/80 hover:bg-[#C5A059] text-white flex items-center justify-center transition-colors z-10"
          aria-label="Volgende afbeelding"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
      
      {/* Dots indicator */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setCurrentIndex(i);
            }}
            className={`w-2 h-2 rounded-full transition-all ${
              i === currentIndex ? "bg-[#C5A059] w-4" : "bg-white/60 hover:bg-white"
            }`}
            aria-label={`Ga naar afbeelding ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

function ReviewCard({ review, index }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = (review.text || "").length > 180;
  return (
    <div className="bg-white p-8 border-l-4 border-[#C5A059] h-full flex flex-col" data-testid={`review-card-${index}`}>
      <div className="flex mb-4">
        {[...Array(5)].map((_, j) => (
          <Star
            key={j}
            className={`w-4 h-4 ${
              j < review.rating
                ? "text-[#C5A059] fill-[#C5A059]"
                : "text-slate-300"
            }`}
          />
        ))}
      </div>
      <p className={`text-slate-600 text-sm leading-relaxed mb-2 ${expanded ? "" : "line-clamp-4"}`}>
        "{review.text}"
      </p>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid={`review-toggle-${index}`}
          className="self-start text-[#005F5F] text-xs font-bold uppercase tracking-wider hover:text-[#C5A059] transition-colors mb-4"
        >
          {expanded ? "Minder lezen" : "Lees meer"}
        </button>
      )}
      <div className="mt-auto">
        <p className="font-bold text-sm text-[#0F172A]">{review.author_name}</p>
        <p className="text-slate-400 text-xs mt-1">{review.relative_time_description}</p>
      </div>
    </div>
  );
}

// Reviews Carousel Component
function ReviewsCarousel({ reviews }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const intervalRef = useRef(null);
  
  // Number of visible cards based on screen size
  const getVisibleCount = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 1024) return 3;
      if (window.innerWidth >= 768) return 2;
      return 1;
    }
    return 3;
  };
  
  const [visibleCount, setVisibleCount] = useState(3);
  
  useEffect(() => {
    const handleResize = () => setVisibleCount(getVisibleCount());
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const maxIndex = Math.max(0, reviews.length - visibleCount);
  
  const nextSlide = useCallback(() => {
    setCurrentIndex(prev => prev >= maxIndex ? 0 : prev + 1);
  }, [maxIndex]);
  
  const prevSlide = useCallback(() => {
    setCurrentIndex(prev => prev <= 0 ? maxIndex : prev - 1);
  }, [maxIndex]);
  
  // Auto-play
  useEffect(() => {
    if (isAutoPlaying && reviews.length > visibleCount) {
      intervalRef.current = setInterval(nextSlide, 4000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isAutoPlaying, nextSlide, reviews.length, visibleCount]);
  
  const handleManualNav = (direction) => {
    setIsAutoPlaying(false);
    if (direction === 'next') nextSlide();
    else prevSlide();
    // Resume auto-play after 8 seconds
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };
  
  return (
    <div
      className="relative"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
      data-testid="reviews-carousel"
    >
      {/* Navigation Arrows */}
      <button
        onClick={() => handleManualNav('prev')}
        className="absolute -left-4 md:-left-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-[#005F5F] hover:bg-[#C5A059] text-white flex items-center justify-center transition-colors shadow-lg"
        aria-label="Vorige review"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        onClick={() => handleManualNav('next')}
        className="absolute -right-4 md:-right-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-[#005F5F] hover:bg-[#C5A059] text-white flex items-center justify-center transition-colors shadow-lg"
        aria-label="Volgende review"
      >
        <ChevronRight className="w-6 h-6" />
      </button>
      
      {/* Reviews Container */}
      <div className="overflow-hidden mx-6 md:mx-8">
        <div 
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentIndex * (100 / visibleCount)}%)` }}
        >
          {reviews.map((review, i) => (
            <div 
              key={i} 
              className="flex-shrink-0 px-3"
              style={{ width: `${100 / visibleCount}%` }}
            >
              <ReviewCard review={review} index={i} />
            </div>
          ))}
        </div>
      </div>
      
      {/* Dots Indicator */}
      <div className="flex justify-center gap-2 mt-8">
        {Array.from({ length: maxIndex + 1 }).map((_, i) => (
          <button
            key={i}
            onClick={() => {
              setCurrentIndex(i);
              setIsAutoPlaying(false);
              setTimeout(() => setIsAutoPlaying(true), 8000);
            }}
            className={`w-2 h-2 rounded-full transition-all ${
              i === currentIndex ? "bg-[#C5A059] w-6" : "bg-slate-300 hover:bg-slate-400"
            }`}
            aria-label={`Ga naar review ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

const reasons = [
  { icon: Clock, label: "8+ jaar ervaring", desc: "Bewezen track record" },
  { icon: Users, label: "9 vakmannen", desc: "Gedreven professionals" },
  { icon: Award, label: "Hoogwaardige materialen", desc: "Alleen het beste" },
  { icon: Shield, label: "Betrouwbare planning", desc: "Altijd op tijd" },
];

const werkgebied = [
  "Amsterdam", "Rotterdam", "Utrecht", "Den Haag",
  "Hilversum", "Amersfoort", "Almere", "Haarlem",
  "Leiden", "Zaanstad", "Lelystad", "Loosdrecht", "Breukelen"
];

function useSectionReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("visible");
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return ref;
}

function SectionReveal({ children, className = "" }) {
  const ref = useSectionReveal();
  return (
    <div ref={ref} className={`section-reveal ${className}`}>
      {children}
    </div>
  );
}

export default function HomePage() {
  const reviews = useGoogleReviews();
  const [adminPhotos, setAdminPhotos] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Load admin photos for all services
    const serviceKeys = ["aannemer", "badkamer", "stukadoor", "schilderwerk", "timmerwerk"];
    serviceKeys.forEach(service => {
      axios.get(`${API}/website-photos/${service}`)
        .then(res => {
          const photos = res.data || [];
          if (photos.length > 0) {
            const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
            setAdminPhotos(prev => ({
              ...prev,
              [service]: photos.map(p => p.url.startsWith('http') ? p.url : `${baseUrl}${p.url}`)
            }));
          }
        })
        .catch(() => {});
    });
  }, []);

  // Combine hardcoded images with admin photos
  const getServiceImages = (serviceKey) => {
    const baseImages = serviceImages[serviceKey] || [];
    const extraImages = adminPhotos[serviceKey] || [];
    return [...baseImages, ...extraImages];
  };

  return (
    <div data-testid="home-page">
      <SEO
        title="Aannemer Breukelen | Renovatie & Bouw – Cardoso Bouw en Onderhoud"
        description="Cardoso Bouw en Onderhoud is een professioneel aannemersbedrijf in Breukelen. Wij verzorgen renovaties, badkamer renovaties, stukadoor werk, schilderwerk en timmerwerk. Vraag vandaag nog een vrijblijvende offerte aan."
        keywords="aannemer Breukelen, bouwbedrijf Breukelen, renovatie Breukelen, woning renovatie Breukelen, badkamer renovatie Breukelen, stukadoor Breukelen, schilderwerk Breukelen, timmerwerk Breukelen, aannemer Utrecht, aannemer Loosdrecht, aannemer Noord-Holland, aannemer Zuid-Holland, aannemer Flevoland"
      />
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img
            src={IMAGES.hero}
            alt="Bouwproject renovatie Breukelen - Cardoso Bouw en Onderhoud"
            className="w-full h-full object-cover"
            fetchPriority="high"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-32">
          <div className="max-w-2xl">
            <p className="text-[#C5A059] font-bold uppercase tracking-[0.25em] text-sm mb-6 animate-fade-in-up">
              Cardoso Bouw en Onderhoud
            </p>
            <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white uppercase leading-tight mb-6 animate-fade-in-up animation-delay-200">
              Bouwen met vertrouwen en vakmanschap
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed mb-10 max-w-xl animate-fade-in-up animation-delay-400">
              Complete renovaties en bouwprojecten uitgevoerd door een ervaren team van vakmannen.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-600">
              <Link
                to="/contact"
                data-testid="hero-cta-offerte"
                className="bg-[#C5A059] hover:bg-[#005F5F] text-white font-bold uppercase tracking-wider px-8 py-4 text-sm transition-all duration-300 hover:-translate-y-1 shadow-lg hover:shadow-xl"
              >
                Gratis offerte aanvragen
              </Link>
              <a
                href="https://wa.me/31685071009"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-whatsapp"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#005F5F] font-bold uppercase tracking-wider px-8 py-4 text-sm transition-all duration-300 flex items-center gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent" />
      </section>

      {/* ABOUT */}
      <section data-testid="about-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <SectionReveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7">
                <p className="text-[#C5A059] font-bold uppercase tracking-[0.2em] text-sm mb-4">Over ons</p>
                <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] uppercase mb-6">
                  Ervaren bouwbedrijf in Breukelen
                </h2>
                <p className="text-slate-600 text-base leading-relaxed mb-6 max-w-lg">
                  Cardoso Bouw en Onderhoud is een ervaren bouwbedrijf in Breukelen. Met meer dan 8 jaar ervaring en een team van 9 vakmannen leveren wij hoogwaardige renovaties en bouwprojecten.
                </p>
                <div className="grid grid-cols-2 gap-6">
                  {["Betrouwbaarheid", "Kwaliteit", "Duidelijke communicatie", "Professionele afwerking"].map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-[#C5A059] flex-shrink-0" />
                      <span className="text-sm font-medium text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="relative">
                    <img
                    src="https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/29bm7afz_ff4bf38e-207d-4edd-b920-5f61b18bd263.jpeg"
                    alt="Aannemer Breukelen - Cardoso Bouw en Onderhoud renovatieproject"
                    className="w-full h-80 object-cover"
                    loading="lazy"
                  />
                  <div className="absolute -bottom-6 -left-6 bg-[#005F5F] text-white p-6">
                    <div className="font-['Barlow_Condensed'] text-4xl font-bold">8+</div>
                    <div className="text-sm text-white/80">Jaar ervaring</div>
                  </div>
                </div>
              </div>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* SEO TEXT */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <SectionReveal>
            <div className="max-w-3xl">
              <h2 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl font-bold text-[#0F172A] uppercase mb-6">
                Uw aannemer in Breukelen en omgeving
              </h2>
              <div className="text-slate-600 text-base leading-relaxed space-y-4">
                <p>
                  Cardoso Bouw en Onderhoud is een professioneel bouwbedrijf gevestigd in Breukelen. 
                  Met meer dan 8 jaar ervaring en een team van 9 vakmannen verzorgen wij complete 
                  renovaties en bouwprojecten.
                </p>
                <p>Wij zijn gespecialiseerd in:</p>
                <ul className="list-none space-y-2 ml-0">
                  {["complete renovaties", "badkamer renovaties", "stukadoor werk", "schilderwerk binnen en buiten", "timmerwerk"].map((item) => (
                    <li key={item} className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-[#C5A059] flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p>
                  Wij werken voor klanten in heel Noord-Holland, Zuid-Holland, Utrecht en Flevoland. Wij komen graag bij u langs voor een vrijblijvende offerte.
                </p>
              </div>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="services-section" className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <SectionReveal>
            <p className="text-[#C5A059] font-bold uppercase tracking-[0.2em] text-sm mb-4">Onze diensten</p>
            <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] uppercase mb-12">
              Wat wij doen
            </h2>
          </SectionReveal>
          <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6">
            {services.map((service, i) => (
              <SectionReveal key={service.title} className={`md:col-span-4 ${service.span}`}>
                <Link
                  to={service.path}
                  data-testid={`service-card-${service.path.replace("/", "")}`}
                  className="group block h-full"
                >
                  <div className="service-card-hover relative overflow-hidden bg-white border border-slate-200 hover:border-[#C5A059] h-full flex flex-col">
                    <div className="relative">
                      <ServiceCardCarousel images={getServiceImages(service.serviceKey)} title={service.title} />
                      <div className="absolute bottom-4 left-4 z-10">
                        <service.icon className="w-8 h-8 text-[#C5A059]" />
                      </div>
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-[#0F172A] mb-2 group-hover:text-[#005F5F] transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-slate-500 text-sm leading-relaxed flex-1">{service.desc}</p>
                      <div className="mt-4 flex items-center gap-2 text-[#C5A059] font-bold uppercase tracking-wider text-xs">
                        Meer informatie
                        <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section data-testid="why-section" className="py-20 md:py-32 bg-[#005F5F]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <SectionReveal>
            <p className="text-[#C5A059] font-bold uppercase tracking-[0.2em] text-sm mb-4">Waarom Cardoso?</p>
            <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase mb-16">
              Waarom klanten voor ons kiezen
            </h2>
          </SectionReveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {reasons.map((reason, i) => (
              <SectionReveal key={reason.label}>
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#C5A059] mb-6">
                    <reason.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white uppercase mb-2">
                    {reason.label}
                  </h3>
                  <p className="text-white/60 text-sm">{reason.desc}</p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* WORK AREA */}
      <section data-testid="werkgebied-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <SectionReveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-[#C5A059] font-bold uppercase tracking-[0.2em] text-sm mb-4">Werkgebied</p>
                <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] uppercase mb-6">
                  Noord- & Zuid-Holland, Utrecht, Flevoland
                </h2>
                <p className="text-slate-600 text-base leading-relaxed mb-8">
                  Wij komen graag bij u langs voor een vrijblijvende offerte. Ons werkgebied:
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {werkgebied.map((plaats) => (
                    <div key={plaats} className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-[#C5A059] flex-shrink-0" />
                      <span className="text-sm font-medium text-slate-700">{plaats}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="h-80 overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d78000!2d4.9899!3d52.1729!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1snl!2snl"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Werkgebied Cardoso Bouw en Onderhoud - Breukelen"
                />
              </div>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* REVIEWS CAROUSEL */}
      <section data-testid="reviews-section" className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <SectionReveal>
            <p className="text-[#C5A059] font-bold uppercase tracking-[0.2em] text-sm mb-4">Reviews</p>
            <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] uppercase mb-4">
              Wat onze klanten zeggen
            </h2>
            {reviews.rating > 0 && (
              <div className="flex items-center gap-3 mb-12">
                <a 
                  href={reviews.maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="reviews-rating-link"
                  className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                >
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-5 h-5 ${
                          i < Math.round(reviews.rating)
                            ? "text-[#C5A059] fill-[#C5A059]"
                            : "text-slate-300"
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-slate-600 text-sm" data-testid="reviews-rating-text">
                    {reviews.rating.toFixed(1)} gemiddeld ({reviews.total_reviews} reviews op Google)
                  </span>
                </a>
              </div>
            )}
          </SectionReveal>
          
          {/* Reviews Carousel */}
          {reviews.reviews.length > 0 && (
            <ReviewsCarousel reviews={reviews.reviews} />
          )}

          <div className="flex flex-wrap justify-center gap-4 mt-12">
            <a
              href={reviews.maps_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-google-btn"
              className="bg-[#005F5F] hover:bg-[#C5A059] text-white font-bold uppercase tracking-wider px-8 py-4 text-sm transition-all duration-300 flex items-center gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              Bekijk alle reviews op Google
            </a>
            <a
              href={writeReviewUrl(reviews.place_id)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-write-btn"
              className="bg-transparent border-2 border-[#005F5F] text-[#005F5F] hover:bg-[#005F5F] hover:text-white font-bold uppercase tracking-wider px-8 py-4 text-sm transition-all duration-300 flex items-center gap-2"
            >
              <PenLine className="w-4 h-4" />
              Schrijf een review
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="py-20 md:py-32 bg-[#005F5F]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 text-center">
          <SectionReveal>
            {/* Gratis & Vrijblijvend Badge */}
            <div className="inline-flex items-center gap-2 bg-green-500 text-white text-sm font-bold uppercase tracking-wider px-4 py-2 rounded-full mb-6">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              Gratis & Vrijblijvend
            </div>
            
            <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase mb-6">
              Vraag vandaag nog een offerte aan
            </h2>
            <p className="text-white/70 text-base mb-10 max-w-lg mx-auto">
              Wij komen graag bij u langs voor een vrijblijvend gesprek over uw project.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="tel:+31685071009"
                data-testid="cta-phone"
                className="bg-[#C5A059] hover:bg-white hover:text-[#005F5F] text-white font-bold uppercase tracking-wider px-8 py-4 text-sm transition-all duration-300 flex items-center gap-2"
              >
                <Phone className="w-4 h-4" />
                Bellen
              </a>
              <a
                href="https://wa.me/31685071009"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-whatsapp"
                className="bg-[#25D366] hover:bg-[#1da851] text-white font-bold uppercase tracking-wider px-8 py-4 text-sm transition-all duration-300 flex items-center gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
              <a
                href="mailto:info@cardosobouw.nl"
                data-testid="cta-email"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#005F5F] font-bold uppercase tracking-wider px-8 py-4 text-sm transition-all duration-300 flex items-center gap-2"
              >
                <Mail className="w-4 h-4" />
                Email
              </a>
            </div>
          </SectionReveal>
        </div>
      </section>
    </div>
  );
}
